import type { Ref } from 'vue';

import { ref, watch } from 'vue';

interface UseTimeInputProps {
  modelValue: string;
  options: string[];
  disabled: boolean;
}

export interface TimeInputEmits {
  (e: 'update:modelValue', value: string): void;
  (e: 'change', value: string): void;
  (e: 'blur', value: string): void;
  (e: 'focus', value: string): void;
}

export function useTimeInput(props: UseTimeInputProps, emit: TimeInputEmits, input: Ref<HTMLInputElement | null>) {
  const inputValue = ref<string>(props.modelValue);
  const isFocused = ref(false);
  const isTextSelected = ref(false);
  const lastValidValue = ref('');
  // Дедупликация эмитов update:modelValue. Без неё клик по опции дропдауна шлёт
  // два эмита (selectOption + программный blur), второй уходит в parent с тем же
  // значением и иногда ломает downstream (например, replaceSlot не находит слот
  // по поменявшемуся идентифицирующему start).
  const lastEmittedValue = ref<string>(props.modelValue);

  /**
   * Валидация времени в формате HH:mm
   */
  const isValidTimeFormat = (time: string): boolean => {
    const timeRegex = /^([0-1]?[0-9]|2[0-3]):([0-5][0-9])$/;
    return timeRegex.test(time);
  };

  /**
   * Проверка валидности значения (в опциях или корректный формат)
   */
  const isValidValue = (value: string): boolean => {
    if (!value) return true;

    if (props.options.length > 0) {
      return props.options.includes(value);
    }

    return isValidTimeFormat(value);
  };

  // Инициализация lastValidValue
  if (props.modelValue && isValidValue(props.modelValue)) {
    lastValidValue.value = props.modelValue;
  }

  // Синхронизация с modelValue
  watch(
    () => props.modelValue,
    (newValue) => {
      inputValue.value = newValue;
      lastEmittedValue.value = newValue;

      if (newValue && isValidValue(newValue)) {
        lastValidValue.value = newValue;
      }
    },
    { immediate: true },
  );

  /**
   * Автоматическое форматирование ввода
   */
  const formatTimeInput = (value: string): string => {
    const raw = value.replace(/\D/g, '');
    // Часов от 30 не бывает: «8» сразу становится «08», следующие цифры идут в минуты.
    const digits = /^[3-9]/.test(raw) ? `0${raw}` : raw;

    if (digits.length === 0) return '';
    if (digits.length <= 2) return digits;

    return `${digits.slice(0, 2)}:${digits.slice(2, 4)}`;
  };

  /** Дописывает неполный ввод: «8» → 08:00, «18» → 18:00, «12:3» → 12:30. */
  const completeTime = (value: string): string => {
    if (!value) return value;

    const [hours = '', minutes = ''] = formatTimeInput(value).split(':');

    return `${hours.padStart(2, '0')}:${minutes.padEnd(2, '0')}`;
  };

  /**
   * Обработка ввода (для случаев вставки из буфера)
   */
  const handleInput = (event: Event) => {
    const target = event.target as HTMLInputElement;
    const rawValue = target.value;

    isTextSelected.value = false;

    if (rawValue.length > 1) {
      const formattedValue = formatTimeInput(rawValue);
      inputValue.value = formattedValue;
      emit('change', formattedValue);
    }
  };

  /**
   * Обработка нажатий клавиш
   */
  const handleKeyDown = (event: KeyboardEvent) => {
    const isDigit = /^[0-9]$/.test(event.key);
    if (isDigit || event.key === 'Backspace') {
      event.preventDefault();

      const target = event.target as HTMLInputElement;
      const selectionStart = target.selectionStart || 0;
      const selectionEnd = target.selectionEnd || 0;
      const currentValue = inputValue.value;

      if (isTextSelected.value || (selectionStart === 0 && selectionEnd === currentValue.length)) {
        inputValue.value = isDigit ? formatTimeInput(event.key) : '';
        isTextSelected.value = false;
      } else if (selectionStart !== selectionEnd) {
        const beforeSelection = currentValue.slice(0, selectionStart);
        const afterSelection = currentValue.slice(selectionEnd);
        if (isDigit) {
          const newValue = beforeSelection + event.key + afterSelection;
          const digitsCount = newValue.replace(/\D/g, '').length;
          if (digitsCount <= 4) {
            inputValue.value = newValue;
          }
          setTimeout(() => {
            const newPosition = selectionStart + 1;
            target.setSelectionRange(newPosition, newPosition);
          }, 0);
        } else {
          inputValue.value = beforeSelection + afterSelection;
        }
      } else if (isDigit) {
        if (currentValue.includes(':')) {
          const [hours, minutes] = currentValue.split(':');
          if (minutes && minutes.length < 2) {
            inputValue.value = `${hours}:${minutes}${event.key}`;
          } else if (hours && hours.length < 2) {
            inputValue.value = `${hours}${event.key}:${minutes}`;
          }
        } else {
          const digits = currentValue.replace(/\D/g, '');
          if (digits.length < 4) {
            inputValue.value = formatTimeInput(digits + event.key);
          }
        }
      } else {
        const digits = currentValue.replace(/\D/g, '');
        if (digits.length > 0) {
          const newDigits = digits.slice(0, -1);
          inputValue.value = newDigits.length > 0 ? formatTimeInput(newDigits) : '';
        }
      }

      emit('change', inputValue.value);
      return;
    }

    if (!/^(Backspace|Delete|ArrowLeft|ArrowRight|Tab|Enter|Escape)$/.test(event.key)) {
      event.preventDefault();
    }
  };

  /**
   * Обработка фокуса
   */
  const handleFocus = () => {
    if (props.disabled) return;

    isFocused.value = true;

    if (input.value && inputValue.value) {
      input.value.select();
      isTextSelected.value = true;
    }

    emit('focus', inputValue.value);
  };

  /**
   * Обработка потери фокуса
   */
  const handleBlur = () => {
    isFocused.value = false;

    let finalValue = completeTime(inputValue.value);

    if (!isValidValue(finalValue)) {
      finalValue = lastValidValue.value;
    } else if (finalValue) {
      lastValidValue.value = finalValue;
    }

    inputValue.value = finalValue;

    if (input.value) {
      input.value.value = finalValue;
    }

    if (finalValue !== lastEmittedValue.value) {
      lastEmittedValue.value = finalValue;
      emit('update:modelValue', finalValue);
    }
    emit('blur', finalValue);
  };

  return {
    inputValue,
    isFocused,
    isTextSelected,
    lastValidValue,
    lastEmittedValue,
    isValidTimeFormat,
    isValidValue,
    formatTimeInput,
    handleInput,
    handleKeyDown,
    handleFocus,
    handleBlur,
  };
}
